import _ from 'underscore';
import { useEffect, useMemo } from 'react';
import deepmerge from 'deepmerge';
import { UserUtils } from '@bingads-webui-clientcenter/entity-utils';
import { Forms, fullOptionalFormControls, AddressSelectorOptions } from '../constants';
import { getSelectedABL } from '../utils';

export const useFormControls = ({
  formControls,
  countryCode,
  userCountryCode,
  countryList,
  businessLocationList,
  accountData,
  accountLoading,
  provinceLoading,
  userProvinceLoading,
  addressLoading,
  customerPermissionsLoading,
  timeZones,
  timeZoneDataLoading,
  currencyList,
  currencyDataLoading,
  addressList,
  prefillAddressId,
  isReadonly,
  forceBusinessAddressOpen,
  setAlwaysShowError,
  isEnhancedABLP1Enabled,
  userAddress,
  onlyIncludeBusinessAIVAddresses,
  showEmptyBusinessAIVAddresses,
}) => {
  const isBusinessNameReady = !accountLoading;
  const isCountrySelectorReady = (!_.isEmpty(businessLocationList) || showEmptyBusinessAIVAddresses) && !accountLoading;
  const isPhoneNumberReady = !_.isEmpty(countryList) && !accountLoading;
  const isAddressFormReady = !_.isEmpty(countryCode) && !provinceLoading && !accountLoading;
  const isAddressSelectorReady = (!_.isEmpty(countryCode) || showEmptyBusinessAIVAddresses) && !addressLoading;
  const isUserAddressFormReady = !_.isEmpty(userCountryCode) && !userProvinceLoading;
  const isTaxFormReady = !_.isEmpty(businessLocationList) && !customerPermissionsLoading;
  const isTimeZoneReady = !_.isEmpty(timeZones) && !timeZoneDataLoading;
  const isCurrencySelectorReady = !_.isEmpty(currencyList) && !currencyDataLoading;
  const isAccountCreated = accountData && accountData.isCreated();
  const isFullFormOptional = useMemo(() => accountData && accountData.isManagedByYahoo(), [accountData]);
  const isMissingMasterData = useMemo(() => UserUtils.isMissingMasterData({ Address: userAddress }), [userAddress]);

  const formControlsInitial = useMemo(() => deepmerge(formControls, isFullFormOptional && fullOptionalFormControls()), [formControls, isFullFormOptional]);

  const {
    isSelectedABLInvalid,
    shouldSetAlwaysShowError,
  } = useMemo(() => {
    if (addressLoading) {
      return {};
    }

    const {
      isSelectedAddressValid,
      isSelectedBusinessNameValid,
      isSelectedABLFromList,
    } = getSelectedABL({
      addressId: prefillAddressId,
      addressList,
      countryCode,
    });
    const isSelectedABLInvalidInternal = !isSelectedAddressValid || !isSelectedBusinessNameValid;

    return {
      isSelectedABLInvalid: isSelectedABLInvalidInternal,
      shouldSetAlwaysShowError: isSelectedABLFromList && isSelectedABLInvalidInternal,
    };
  }, [addressLoading, prefillAddressId, addressList, countryCode]);

  useEffect(() => {
    if (shouldSetAlwaysShowError) {
      setAlwaysShowError(true);
    }
  }, [shouldSetAlwaysShowError, setAlwaysShowError]);

  const showBusinessAddressSelector = !isReadonly;
  const showBusinessNameAndAddressForm = isReadonly || forceBusinessAddressOpen || isSelectedABLInvalid;
  const hideAddressForm = isEnhancedABLP1Enabled ? (prefillAddressId === AddressSelectorOptions.selectExistingAddressId) : false;
  const showUserLocationSection = isMissingMasterData && formControlsInitial[Forms.UserLocation].visible && !isReadonly;

  const formControlsCombined = useMemo(() => deepmerge(formControlsInitial, {
    [Forms.BusinessName]: {
      visible: !!(formControlsInitial[Forms.BusinessName].visible && isBusinessNameReady && showBusinessNameAndAddressForm),
    },
    [Forms.PhoneNumber]: {
      visible: !!(formControlsInitial[Forms.PhoneNumber].visible && isPhoneNumberReady),
    },
    [Forms.CountrySelector]: {
      visible: formControlsInitial[Forms.CountrySelector].visible && isCountrySelectorReady,
    },
    [Forms.BusinessAddressSelector]: {
      visible: formControlsInitial[Forms.BusinessAddressSelector].visible && isAddressSelectorReady && showBusinessAddressSelector,
    },
    [Forms.AddressForm]: {
      visible: !!(formControlsInitial[Forms.AddressForm].visible && isAddressFormReady && showBusinessNameAndAddressForm && !hideAddressForm && !formControlsInitial[Forms.BusinessAddressSelector].disabled) && !onlyIncludeBusinessAIVAddresses,
    },
    [Forms.UserLocation]: {
      visible: showUserLocationSection,
    },
    [Forms.UserAddressForm]: {
      visible: !!(formControlsInitial[Forms.UserAddressForm].visible && isUserAddressFormReady && showUserLocationSection),
    },
    [Forms.TaxForm]: {
      visible: !!(formControlsInitial[Forms.TaxForm].visible && isTaxFormReady),
    },
    [Forms.TimeZone]: {
      visible: !!(formControlsInitial[Forms.TimeZone].visible && isTimeZoneReady),
    },
    [Forms.CurrencySelector]: {
      visible: !!(formControlsInitial[Forms.CurrencySelector].visible && isCurrencySelectorReady),
      disabled: !!isAccountCreated || formControlsInitial[Forms.CurrencySelector].disabled,
    },
  }), [formControlsInitial, hideAddressForm, isAccountCreated, isAddressFormReady, isAddressSelectorReady, isBusinessNameReady, isCountrySelectorReady, isCurrencySelectorReady, isPhoneNumberReady, isTaxFormReady, isTimeZoneReady, isUserAddressFormReady, onlyIncludeBusinessAIVAddresses, showBusinessAddressSelector, showBusinessNameAndAddressForm, showUserLocationSection]);

  return formControlsCombined;
};
